import * as React from "react";
import { format } from "date-fns";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import MuiTab from "@material-ui/core/Tab";

import {
  List,
  TextField,
  FunctionField,
  ReferenceArrayInput,
  ReferenceInput,
  SelectInput,
  SelectArrayInput,
  AutoCompleteArrayInput,
  FilterButton,
  Filter
} from "jazasoft";

import Tabs from "../../../../components/tab/Tabs";
import Datagrid from "../../../../components/Datagrid";
import TooltipTextField from "../../../../components/TooltipTextField";

import { useQuery, useSafeState, useSetting } from "../../../../hooks";
import { colorLightGreen, colorOrange, colorSecondary, colorTomatoRed } from "../../../../utils/colors";
import { getStateChoices } from "../../../../constants";
import { inputOptions, isEmpty, listFromCsv, toFixed } from "../../../../utils/helpers";
import SettingKeys from "../../../../utils/SettingKeys";
import { Entity, OState } from "../../../../utils/types";
import useWarehouse from "../../../../hooks/useWarehouse";

const renderAlllocAssocQty = (record, field) => {
  if (isEmpty(record) || isEmpty(record[field])) return "-";
  const grnQty = isEmpty(record.grnQty) ? record.invoiceQty || 0 : record.grnQty;
  const value = record[field];
  return (
    <div
      style={{
        backgroundColor: value >= grnQty ? colorLightGreen.dark : value > 0 ? colorOrange.dark : colorTomatoRed.dark,
        color: "white",
        textAlign: "center",
        padding: "4px 12px",
        borderRadius: 4
      }}>
      {toFixed(value, 0)}
    </div>
  );
};

const OrderStateValue = Object.keys(OState)
  .map(key => OState[key])
  .reduce((acc, e) => ({ ...acc, [e.state]: e.stateValue }), {});

const renderState = record => {
  const stateList = record?.states?.split(",");
  const state = stateList?.map(state => ({ state, value: OrderStateValue[state] })).sort((a, b) => a.value - b.value)[0]?.state;
  return state + `${stateList?.length > 1 ? "*" : ""}`;
};

const parse =
  tabIdx =>
  ({ state, productName, seasonId, customerId, supplierId, warehouse }) => {
    return tabIdx === 0
      ? {
          "customer.id": customerId,
          "season.id": seasonId,
          products: isEmpty(productName) ? null : `*${productName}*`,
          states: state,
          warehouses: warehouse
        }
      : { "supplier.id": supplierId, warehouse };
  };

const filters = (tabIdx, productList, whList) => (
  <Filter>
    {whList?.length > 2 && <SelectInput label="Warehouse" source="warehouse" choices={whList} {...inputOptions(12)} />}
    {tabIdx === 0 && <SelectInput label="Order Status" source="state" choices={getStateChoices()} {...inputOptions(12)} />}
    {tabIdx === 0 && (
      <SelectInput label="Products" source="productName" choices={productList.map(p => ({ id: p.name, name: p.name }))} {...inputOptions(12)} />
    )}
    {tabIdx === 0 && (
      <ReferenceArrayInput
        label="Season"
        resource="seasons"
        source="seasonId"
        reference="seasons"
        pageSize={100}
        sort={{ field: "name", order: "asc" }}
        {...inputOptions(12)}>
        <SelectArrayInput />
      </ReferenceArrayInput>
    )}
    {tabIdx === 0 && (
      <ReferenceArrayInput
        label="Customer"
        resource="customers"
        source="customerId"
        reference="customers"
        sort={{ field: "name", order: "asc" }}
        {...inputOptions(12)}>
        <AutoCompleteArrayInput disablePortal={true} />
      </ReferenceArrayInput>
    )}
    {tabIdx === 1 && (
      <ReferenceInput
        label="Supplier"
        source="supplierId"
        reference="suppliers"
        pageSize={100}
        sort={{ field: "name", order: "asc" }}
        {...inputOptions(12)}>
        <SelectInput />
      </ReferenceInput>
    )}
  </Filter>
);

const SubTab = withStyles(theme => ({
  root: {
    minHeight: 36,
    backgroundColor: "white",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
    color: "#333333",
    borderRadius: 20,
    border: "1px solid rgba(200, 200, 200, 1)",
    marginRight: theme.spacing(1)
  },
  selected: {
    minHeight: 36,
    backgroundColor: "#BFDDF8",
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(14),
    color: colorSecondary.dark,
    borderRadius: 20,
    border: "1px solid #BFDDF8",
    marginRight: theme.spacing(1)
  }
}))(props => <MuiTab disableRipple {...props} />);

const MaterialTransfer = props => {
  const [tabIdx, setTabIdx] = useSafeState(0);

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);
  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const { allWhList } = useWarehouse();

  const { data: productList = [] } = useQuery({ url: "products", params: { size: 10000 } });

  const rowClick = (rowId, basePath) => {
    if (tabIdx === 0) {
      props.history.push(`${basePath}/${rowId}/po-view`);
    } else if (tabIdx === 1) {
      props.history.push(`${basePath}/${rowId}/invoice-view`);
    } else {
      props.history.push(`${basePath}/${rowId}/item-view`);
    }
  };

  const whChoices = [{ id: "all", name: "All Warehouses" }, ...allWhList.map(({ code, name }) => ({ id: code, name }))];

  return (
    <div>
      <List
        {...props}
        key={tabIdx}
        resource={tabIdx === 0 ? "orders" : tabIdx === 1 ? "fabric-items" : "fabric-colors"}
        i18nKey={tabIdx === 0 ? "orders" : tabIdx === 1 ? "fabricItems" : "fabricColors"}
        title={
          <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
            <SubTab label="By Order" />
            <SubTab label="By Invoice" />
            <SubTab label="By Item" />
          </Tabs>
        }
        requestConfig={{ params: { tab: "wh-quality" } }}
        searchKeys={
          tabIdx === 0
            ? ["poRef", "customerPoRef", "styles"]
            : tabIdx === 1
            ? ["invoice.invoiceNo", "invoice.erpInvoiceNo", "grn", "fabricPo", "fabric.itemCode", "fabric.itemDesc"]
            : ["fabric.itemCode", "fabric.articleName"]
        }
        actions={({ resource }) => (
          <div>
            <FilterButton showLabel={false} resource={resource} />
          </div>
        )}
        filters={filters(tabIdx, productList, whChoices)}
        parse={parse(tabIdx)}>
        <Datagrid i18nKey={tabIdx === 0 ? "orders" : "fabricItems"} rowClick={rowClick}>
          {tabIdx === 0 && orderFields.includes("poRef") && <TextField source="poRef" />}
          {tabIdx === 0 && orderFields.includes("customerPoRef") && <TextField source="customerPoRef" />}
          {tabIdx === 0 && orderFields.includes("customer") && <TextField source="customer.name" />}
          {tabIdx === 0 && orderFields.includes("products") && <TextField source="products" />}
          {tabIdx === 0 && orderFields.includes("styles") && <TextField source="styles" />}
          {tabIdx === 0 && orderFields.includes("season") && <TextField source="season.name" />}
          {tabIdx === 0 && allWhList?.length > 1 && (
            <FunctionField
              source="warehouses"
              render={record =>
                allWhList
                  .filter(wh => listFromCsv(record?.warehouses).includes(wh.code))
                  .map(u => u.name)
                  .join(", ")
              }
            />
          )}

          {tabIdx === 0 && orderFields.includes("status") && <FunctionField source="status" render={renderState} />}
          {tabIdx === 1 && invoiceFields.includes("invoiceNo") && (
            <FunctionField source="invoiceNo" length={15} render={record => record?.invoice?.invoiceNo} />
          )}
          {tabIdx === 1 && invoiceFields.includes("erpInvoiceNo") && (
            <TooltipTextField source="erpInvoiceNo" length={15} render={record => record?.invoice?.erpInvoiceNo} />
          )}
          {tabIdx === 1 && fabricItemFields.includes("grn") && <TooltipTextField source="grn" length={15} />}
          {tabIdx === 1 && fabricFields.includes("itemCode") && (
            <TooltipTextField source="itemCode" length={15} render={record => record?.fabric?.itemCode} />
          )}
          {tabIdx === 1 && fabricFields.includes("itemDesc") && (
            <TooltipTextField source="itemDesc" length={15} render={record => record?.fabric?.itemDesc} />
          )}
          {tabIdx === 1 && fabricFields.includes("articleName") && (
            <TooltipTextField source="articleName" length={15} render={record => record?.fabric?.articleName} />
          )}
          {tabIdx === 1 && fabricItemFields.includes("color") && (
            <TooltipTextField
              source="color"
              length={15}
              render={record => `${record.color}${!isEmpty(record.colorCode) ? `(${record.colorCode})` : ""}`}
            />
          )}
          {tabIdx === 1 && fabricItemFields.includes("colorShade") && <TextField source="colorShade" />}
          {tabIdx === 1 && fabricItemFields.includes("fpo") && <TooltipTextField source="fpo" length={15} />}
          {tabIdx === 1 && fabricItemFields.includes("supplier") && (
            <TooltipTextField source="supplier" length={15} render={record => record?.invoice?.supplier?.name} />
          )}
          {tabIdx === 1 && fabricItemFields.includes("grnDate") && (
            <FunctionField source="grnDate" render={record => (isEmpty(record?.grnDate) ? "-" : format(record.grnDate, "dd MMM, yy"))} />
          )}
          {tabIdx === 1 && allWhList?.length > 1 && (
            <TooltipTextField source="warehouse" length={15} render={record => allWhList.find(wh => record?.warehouse === wh.code)?.name} />
          )}
          {tabIdx === 1 && fabricItemFields.includes("invoiceQty") && <TextField source="invoiceQty" />}
          {tabIdx === 1 && fabricItemFields.includes("grnQty") && (
            <FunctionField source="grnQty" render={record => (isEmpty(record?.grnQty) ? "-" : toFixed(record.grnQty, 0))} />
          )}
          {/* {tabIdx === 1 && fabricItemFields.includes("associatedQty") && <TextField source="associatedQty" />}
          {tabIdx === 1 && fabricItemFields.includes("allocatedQty") && <TextField source="allocatedQty" />} */}
          {tabIdx === 1 && <FunctionField source="associatedQty" render={record => renderAlllocAssocQty(record, "associatedQty")} />}
          {tabIdx === 1 && <FunctionField source="allocatedQty" render={record => renderAlllocAssocQty(record, "allocatedQty")} />}

          {tabIdx === 2 && fabricFields.includes("itemCode") && (
            <TooltipTextField source="fabric.itemCode" label="resources.fabrics.fields.itemCode" length={15} />
          )}
          {tabIdx === 2 && fabricFields.includes("itemDesc") && (
            <TooltipTextField source="fabric.itemDesc" label="resources.fabrics.fields.itemDesc" length={15} />
          )}
          {tabIdx === 2 && fabricFields.includes("articleName") && (
            <TooltipTextField source="fabric.articleName" label="resources.fabrics.fields.articleName" length={15} />
          )}
          {tabIdx === 2 && fabricFields.includes("classification") && (
            <TooltipTextField source="fabric.classification" label="resources.fabrics.fields.classification" length={15} />
          )}
          {tabIdx === 2 && fabricFields.includes("fabricCategory") && (
            <TooltipTextField source="fabric.fabricCategory.name" label="resources.fabrics.fields.fabricCategoryName" length={15} />
          )}
          {tabIdx === 2 && fabricFields.includes("fabricPattern") && (
            <TooltipTextField source="fabric.fabricPattern.name" label="resources.fabrics.fields.fabricPatternName" length={15} />
          )}
          {tabIdx === 2 && fabricItemFields.includes("color") && (
            <TooltipTextField source="color" length={30} render={record => `${record.color}${!isEmpty(record.code) ? `(${record.code})` : ""}`} />
          )}
        </Datagrid>
      </List>
    </div>
  );
};

export default MaterialTransfer;
